import { API, AUTH } from '../api'
import { types, flow } from 'mobx-state-tree'
import _ from 'lodash'
const { model, maybeNull, array, string, number, boolean, optional } = types

const RegisteringAccount = model({
  emailAddress: maybeNull(string),
  birthDate: maybeNull(string),
  firstName: maybeNull(string),
  lastName: maybeNull(string),
  password: maybeNull(string),
})

const SubscriptionInfo = model({
  name: maybeNull(string),
  paidBy: maybeNull(string),
  price: maybeNull(number),
  interval: maybeNull(string),
  status: maybeNull(string),
  origin: maybeNull(string),
  startDate: maybeNull(string),
  expirationDate: maybeNull(string),
  promotion: maybeNull(string),
  pricebookId: maybeNull(number),
})

const PaymentInfo = model({
  id: maybeNull(string),
  type: maybeNull(string),
  lastDigits: maybeNull(string),
})

const RetailStore = model({
  retailRegisteredAccount: optional(RegisteringAccount, {}),
  subscriptions: array(SubscriptionInfo),
  subscription: maybeNull(SubscriptionInfo),
  // `subscription` is null both while loading and when none exists; this flag distinguishes them.
  subscriptionsLoaded: optional(boolean, false),
  licenseChecked: optional(boolean, false),
  hasActiveLicense: optional(boolean, false),
  defaultPaymentInfo: optional(PaymentInfo, {}),
  token: maybeNull(string),
  domain3n: maybeNull(string),
})
  .views(self => ({
    /** A retail member confirmed to have no active license. */
    get isUnlicensedRetailMember() {
      return Boolean(self.config?.isRetail) && self.licenseChecked && !self.hasActiveLicense
    },
    get hasPlanAccess() {
      return !self.isUnlicensedRetailMember
    },
    // Uses subscription history, so canceled or expired subscriptions still count.
    get hasEverSubscribed() {
      return self.subscriptions.length > 0
    },
    get subscriptionStatus() {
      if (self.subscription) {
        const expirationDate = new Date(self.subscription.expirationDate)
        const now = new Date()
        if (expirationDate < now) {
          return 'expired'
        }
      }
      return self.subscription?.status
    },
    get subscriptionExpiration() {
      if (self.subscription) {
        const expirationDate = new Date(self.subscription.expirationDate)
        return expirationDate.toLocaleDateString()
      }
      return new Date().toLocaleDateString()
    },
  }))
  .actions(self => ({
    setRetailLoginAccount(account) {
      self.loginStore = account
      self.loginStore.username = account.email
    },
    setSubscriptions(subscriptions) {
      self.subscriptions = subscriptions
    },
    selectPricebook: flow(function* (pricebookId) {
      const res = yield API.post(`pricebook/select?pricebookId=${pricebookId}`)
      sessionStorage.setItem('pricebook-id', pricebookId)
      return res
    }),
    clearPricebookSelection() {
      sessionStorage.removeItem('pricebook-id')
    },
    getRetailLoginEmail: flow(function* () {
      yield API.post('/ui/retail/authorize')

      const res = yield AUTH.get('retail/user')
      const data = _.get(res, 'data.username', '')
      self.loginStore.username = data
      return data
    }),
    getSubscriptions: flow(function* () {
      const { data } = yield API.get('subscription/subscription-info')
      // MST's array type rejects null, so a null body is normalized to an empty array.
      self.setSubscriptions(data ?? [])
      self.subscription = data?.[0] ?? null
      self.subscriptionsLoaded = true
      return data
    }),
    // Fail open: if the initial check fails, the member retains plan access.
    getLicenseStatus: flow(function* () {
      try {
        const { data } = yield API.get('license/status')
        self.hasActiveLicense = Boolean(data?.hasActiveLicense)
        self.licenseChecked = true
      } catch (err) {
        console.error(err)
      }
    }),
    /** Resolves with the Stripe URL to send the browser to; does not navigate. */
    createRetailCheckoutSession: flow(function* (pricebookId) {
      const { data } = yield API.post(`checkout-session/create?pricebookId=${pricebookId}`)
      return data?.url
    }),
    getDefaultPayment: flow(function* () {
      const paymentMethods = yield API.get('payment')
      const { data } = yield API.get('payment/default-payment')
      const defaultPayment = paymentMethods.data.find(paymentMethod => paymentMethod.id === data)
      self.defaultPaymentInfo = defaultPayment
      return defaultPayment
    }),
    getPricebookId() {
      return sessionStorage.getItem('pricebook-id')
    },
    async discontinueRetailSubscription() {
      await API.delete('subscription/cancel/member')
      await this.getSubscriptions()
    },
    async reinstateRetailSubscription() {
      // Stripe subscriptions must be reactivated with their original pricebook.
      await API.post(`subscription/resubscribe/${self.subscription?.pricebookId}`)
      await this.getSubscriptions()
      // Resubscribing reactivates the license, so refresh plan access.
      await this.getLicenseStatus()
    },
    get3nDomain: flow(function* () {
      const { data } = yield API.get('domain/3n')
      self.domain3n = data.domain
      return data.domain
    }),
  }))

export default RetailStore
